<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="traders"
      :headers="traderHeaders"
      :list="traders"
      :total-records="tradersTotalCount"
      table-id="traders-management"
      clickable
      :items-per-page="tradersItemsPerPage"
      :selected-page="tradersPageNumber"
      @refresh-data="onRefreshData"
      @delete-multiselect="onDeleteMultiselect"
      @add-new="onAddNewTrader"
      @table-item-clicked="onItemClick"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager && !item.deleted"
            color="red"
            :icon="IconTrashOutline"
            @action="deleteSingleConfirm(item)" />
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>
    <BaseModalConfirm
      v-if="confirmDeleteSingleModalActive"
      @cancel="confirmDeleteSingleModalActive = false"
      @confirm="onDeleteSingle">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text>
        {{ $t('messages.info.deleteSingle', { item: traderToDeleteSingle?.code }) }}</template
      >
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import router from '@/router';
import { useToastStore } from '@/stores/toast';
import { useStateStore } from '@/stores/state';
import { useLookupsStore } from '@/stores/lookups';
import { useI18n } from 'vue-i18n';
import { getTradersWithConditions, removeTradersByIds } from '@/services/api/traders';
import { v4 as uuidv4 } from 'uuid';
import BaseTable from '@/components/common/BaseTable.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import type { Trader } from '@/types/Traders';
import type { QueryParams, TableFilterData } from '@/types/Base';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

const { t: $t } = useI18n({ useScope: 'global' });
const toastStore = useToastStore();
const traderHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'taxNumber', text: $t('taxNumber'), type: 'text', active: true },
  { value: 'address', text: $t('address'), type: 'text', active: true },
  { value: 'city', text: $t('city'), type: 'text', active: true },
  { value: 'zipCode', text: $t('zipCode'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const traders = ref<Partial<Trader>[]>();
const tradersTotalCount = ref<number>();
const confirmDeleteSingleModalActive = ref(false);
const traderToDeleteSingle = ref<Partial<Trader>>();
const tradersItemsPerPage = ref(50);
const tradersPageNumber = ref(1);
const filterIsActive = ref<boolean | undefined>();

async function loadData(params: QueryParams) {
  const res = await getTradersWithConditions(params);
  if (res) {
    traders.value = res.data;
    tradersTotalCount.value = res.count;
  }
}

const { getMwAccountGroupsLookup, getMWCountriesLookup, getTaxOfficesLookup, getUsersLookup } =
  useLookupsStore();

const { getTableSelectedPage } = useStateStore();

onMounted(async () => {
  await getMWCountriesLookup();
  await getMwAccountGroupsLookup();
  await getTaxOfficesLookup();
  await getUsersLookup();
  const storedTableData = getTableSelectedPage('traders-management');
  if (storedTableData) {
    tradersItemsPerPage.value = storedTableData.itemsPerPage;
    tradersPageNumber.value = storedTableData.page;
  }
  await loadData({
    itemsPerPage: tradersItemsPerPage.value,
    sortColumn: 'code',
    sortAscending: true,
    page: tradersPageNumber.value,
  });
});

async function onRefreshData(data: TableFilterData) {
  const sortColumn = data.column !== undefined ? traderHeaders[data.column]?.value : 'code';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params: QueryParams = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn,
    sortAscending,
    isActive: data.isActive,
  };

  await loadData(params);
}

async function deleteTraders(idsToDelete: string[]) {
  if (idsToDelete.length === 0) {
    toastStore.info({ text: $t('messages.info.selectedItemsCannotBeDeleted') });
    return;
  }
  const res = await removeTradersByIds(idsToDelete);
  if (!res && traders.value)
    traders.value = traders.value.filter((type) => !idsToDelete.includes(type.id || ''));

  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

async function onDeleteSingle() {
  if (!traderToDeleteSingle.value?.id) return;
  confirmDeleteSingleModalActive.value = false;
  await deleteTraders([traderToDeleteSingle.value.id]);
}

async function onDeleteMultiselect(items: Partial<Trader>[]) {
  const idsToDelete = items
    .filter((item) => item.isFromManager)
    .map((item) => item.id)
    .filter((id): id is string => id !== undefined);

  await deleteTraders(idsToDelete);
}

function deleteSingleConfirm(item: Partial<Trader>) {
  traderToDeleteSingle.value = item;
  confirmDeleteSingleModalActive.value = true;
}

function onAddNewTrader() {
  router.push({ name: 'traders-preview', params: { id: uuidv4() } });
}

function onItemClick(item: Partial<Trader>) {
  router.push({ name: 'traders-preview', params: { id: item.id } });
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  onRefreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  onRefreshData({});
}
</script>
